import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
    fetchProspects, fetchProspect, createProspect, updateProspect, deactivateProspect,
    addProspectToEvent, removeProspectFromEvent,
} from '../api/prospectService';
import { Prospect, ProspectInput } from '../../types';
import { useTenantCode } from './useTenantCode';

// D2 — personas de prueba.

// El servidor explica en español por qué algo no se puede (clase llena, plazas
// reservadas para la lista de espera, clase ya empezada…), así que su mensaje se
// muestra tal cual, como en el resto de la app.
const serverMessage = (error: unknown, fallback: string) =>
    (error as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

export const useProspectsQuery = (includeInactive = false) => {
    const tenantCode = useTenantCode();
    return useQuery<Prospect[]>({
        queryKey: ['prospects', tenantCode, includeInactive],
        queryFn: () => fetchProspects(includeInactive),
        enabled: !!tenantCode,
    });
};

export const useProspectQuery = (id?: string) => {
    const tenantCode = useTenantCode();
    return useQuery<Prospect>({
        queryKey: ['prospects', tenantCode, 'detail', id],
        queryFn: () => fetchProspect(id as string),
        enabled: !!tenantCode && !!id,
    });
};

export const useCreateProspectMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (prospect: ProspectInput) => createProspect(prospect),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['prospects'] });
            toast.success('Persona de prueba registrada');
        },
        onError: (error: unknown) => {
            toast.error(serverMessage(error, 'No se pudo registrar a la persona de prueba'));
        },
    });
};

export const useUpdateProspectMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<ProspectInput> }) =>
            updateProspect(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['prospects'] });
            toast.success('Datos actualizados');
        },
        onError: (error: unknown) => {
            toast.error(serverMessage(error, 'No se pudieron actualizar los datos'));
        },
    });
};

export const useDeactivateProspectMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => deactivateProspect(id),
        onSuccess: (data) => {
            // Al darla de baja sale de las clases futuras, así que el calendario
            // también cambia.
            queryClient.invalidateQueries({ queryKey: ['prospects'] });
            queryClient.invalidateQueries({ queryKey: ['eventos'] });
            queryClient.invalidateQueries({ queryKey: ['eventos-date-range'] });
            toast.success(
                data?.removedFrom
                    ? `Persona de prueba dada de baja. Se ha quitado de ${data.removedFrom} clase(s) pendiente(s).`
                    : 'Persona de prueba dada de baja'
            );
        },
        onError: (error: unknown) => {
            toast.error(serverMessage(error, 'No se pudo dar de baja'));
        },
    });
};

/** Apuntar a alguien de prueba a una clase concreta. */
export const useAddProspectToEventMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ eventId, prospectId }: { eventId: string; prospectId: string }) =>
            addProspectToEvent(eventId, prospectId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['eventos'] });
            queryClient.invalidateQueries({ queryKey: ['eventos-date-range'] });
            queryClient.invalidateQueries({ queryKey: ['prospects'] });
            // P6 — una persona de prueba también se marca en la lista.
            queryClient.invalidateQueries({ queryKey: ['class-register'] });
            toast.success('Persona de prueba apuntada a la clase');
        },
        onError: (error: unknown) => {
            toast.error(serverMessage(error, 'No se pudo apuntar a la clase'));
        },
    });
};

export const useRemoveProspectFromEventMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ eventId, prospectId }: { eventId: string; prospectId: string }) =>
            removeProspectFromEvent(eventId, prospectId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['eventos'] });
            queryClient.invalidateQueries({ queryKey: ['eventos-date-range'] });
            queryClient.invalidateQueries({ queryKey: ['prospects'] });
            queryClient.invalidateQueries({ queryKey: ['class-register'] });
            toast.success('Persona de prueba retirada de la clase');
        },
        onError: (error: unknown) => {
            toast.error(serverMessage(error, 'No se pudo retirar de la clase'));
        },
    });
};
